import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useField } from 'formik';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { IFormItem } from '../form-item.type';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormErrorMsg } from '../form-error-msg';
import { trigger } from 'utils/dom';
import clsx from 'clsx';

interface ITextEditor extends IFormItem {
  defaultHeight?: number;
  onChange?: (markup: string) => void;
  disabled?: boolean; // 👈 thêm
}

function convertToEditorState(html: string) {
  const blocksFromHtml = htmlToDraft(html);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

  return EditorState.createWithContent(contentState);
}

export function TextEditor(props: ITextEditor) {
  const [tool, setTool] = useState<boolean>(false);
  const [height, changeHeight] = useState(props.defaultHeight || 180);
  const [field, , helper] = useField(props.name);
  const editorStateRef = useRef<EditorState>(EditorState.createEmpty());
  const editorMarkupRef = useRef<EditorState>();
  const textEditorRef = useRef<HTMLDivElement>();

  const defaultHeight = 78;

  useEffect(() => {
    const initialMarkup = field.value || '';

    editorMarkupRef.current = initialMarkup;
    editorStateRef.current = convertToEditorState(initialMarkup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rdwEditor: HTMLElement = textEditorRef.current.querySelector('.rdw-editor-main');

    rdwEditor.style.height = `${height}px`;
  }, [height]);

  const onEditorStateChange = (editorState: EditorState) => {
    if (props.disabled) return; // 👈 chặn update

    const markup = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    editorStateRef.current = editorState;
    editorMarkupRef.current = markup;
    helper.setValue(markup);
    props.onChange?.(markup);
  };


  if (editorMarkupRef.current !== field.value) {
    const newMarkup = field.value || '';

    editorStateRef.current = convertToEditorState(newMarkup);
    editorMarkupRef.current = newMarkup;
  }

  const heightChanger = useCallback(
    (e) => {
      if (props.disabled) return; // 👈 chặn resize

      const startPageY = e.pageY;

      const handleDrag = (e) => {
        const _height = height + e.pageY - startPageY;

        changeHeight(defaultHeight < _height ? _height : defaultHeight);
      };

      const handleDragEnd = () => {
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
        setTimeout(() => trigger(window, 'resize'), 10);
      };

      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
    },
    [height, props.disabled]
  );

  return (
    <div className='form-control-text-editor' ref={textEditorRef}>
      <button
        tabIndex={-1}
        className='text-editor-resize'
        type='button'
        disabled={props.disabled} // 👈
        onMouseDown={heightChanger}
      />

      <Editor
        editorState={editorStateRef.current}
        onEditorStateChange={onEditorStateChange}
        readOnly={props.disabled}          // 👈 QUAN TRỌNG
        toolbarHidden={props.disabled}     // 👈 Ẩn toolbar khi disable
        wrapperClassName='wrapper-class'
        editorClassName={clsx('editor-class', {
          'editor-disabled': props.disabled,
        })}
        toolbarCustomButtons={
          props.disabled
            ? []
            : [
              <IconFullscreen
                key='fullscreen'
                tool={tool}
                setTool={setTool}
                refEditor={textEditorRef.current}
              />,
            ]
        }
      />

      <FormErrorMsg name={props.name} />
    </div>
  );
}

const IconFullscreen = ({
                          tool,
                          setTool,
                          refEditor,
                        }: {
  refEditor: HTMLDivElement | null;
  tool: boolean;
  setTool: (value: boolean) => void;
}) => {
  useEffect(() => {
    if (!refEditor && !tool) setTool(true);
  }, [setTool, tool, refEditor]);

  const [showFull, setShowFull] = useState(() => {
    return !refEditor?.classList.contains('active-form-editor');
  });

  const toggleFullscreen = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!refEditor) return;

      setShowFull((prev) => !prev);
      refEditor.classList.toggle('active-form-editor');
    },
    [refEditor]
  );

  return (
    <div className='icon-fullscreen' onClick={toggleFullscreen}>
      {showFull ? (
        <i className='fas fa-expand-alt' title='Maximize' />
      ) : (
        <i className='fas fa-compress-alt' title='Minimize' />
      )}
    </div>
  );
};

